import { HttpErrorResponse } from '@angular/common/http';
import { CommonModule } from '@angular/common';
import { Component, Input, type OnChanges, inject } from '@angular/core';
import { CdwButtonModule } from '@cdw-ae/common-ui-components-button';
import { CdwIconModule } from '@cdw-ae/common-ui-components-icon';
import {
  BARE_DOMAIN_ERROR,
  BARE_DOMAIN_REGEX,
  type LicenseCheckResult,
  type SubscriptionFacts,
} from '@cdw-ae/common-data';
import { licenseTierTone } from '../../services/platform-licensing/license-status';
import { PlatformLicensingService } from '../../services/platform-licensing/platform-licensing.service';

/**
 * Shared license-check UI (EDUS-2725 support diagnostic): check + refresh
 * buttons and the resulting Chargify license facts for a domain. Embedded by
 * the customer detail page (prefilled domain) and the License Lookup page
 * (free domain input).
 */
@Component({
  selector: 'cdw-license-check-panel',
  standalone: true,
  imports: [CommonModule, CdwButtonModule, CdwIconModule],
  templateUrl: './license-check-panel.component.html',
  styleUrl: './license-check-panel.component.scss',
})
export class LicenseCheckPanelComponent implements OnChanges {
  private readonly licensingService = inject(PlatformLicensingService);

  @Input() domain: string | null = null;

  readonly tierTone = licenseTierTone;

  checking = false;
  result: LicenseCheckResult | null = null;
  errorMessage: string | null = null;
  /** The domain the displayed result belongs to (shown in the result card). */
  checkedDomain: string | null = null;

  get normalizedDomain(): string {
    return (this.domain ?? '').trim().toLowerCase();
  }

  ngOnChanges(): void {
    // `domain` is the only input, so any change notification means it moved.
    // A displayed result is only valid for the domain it was checked against —
    // clear it as soon as the input domain diverges (lookup-page typing).
    if (
      this.checkedDomain !== null &&
      this.normalizedDomain !== this.checkedDomain
    ) {
      this.result = null;
      this.errorMessage = null;
      this.checkedDomain = null;
    }
  }

  get subscriptionRows(): { sku: string; facts: SubscriptionFacts }[] {
    if (!this.result) {
      return [];
    }
    return Object.entries(this.result.subscriptions)
      .filter(
        (entry): entry is [string, SubscriptionFacts] => !!entry[1]
      )
      .map(([sku, facts]) => ({ sku, facts }));
  }

  /** `refresh` busts the 24h cache first ("customer just renewed" remedy). */
  check(refresh = false): void {
    const domain = this.normalizedDomain;
    if (!domain || this.checking) {
      return;
    }
    // Same rule the server enforces — catch it here instead of a round trip.
    if (!BARE_DOMAIN_REGEX.test(domain)) {
      this.result = null;
      this.errorMessage = BARE_DOMAIN_ERROR;
      return;
    }
    this.checking = true;
    this.errorMessage = null;
    this.licensingService.checkLicense(domain, refresh).subscribe({
      next: (result) => {
        this.checking = false;
        this.result = result;
        this.checkedDomain = domain;
      },
      error: (err: HttpErrorResponse) => {
        this.checking = false;
        this.result = null;
        if (err.status === 503) {
          this.errorMessage =
            'The licensing service is unavailable right now. Please try again.';
        } else if (err.status === 400) {
          this.errorMessage = BARE_DOMAIN_ERROR;
        } else {
          this.errorMessage = 'The license check failed. Please try again.';
        }
      },
    });
  }
}
